import { DropResult } from '@hello-pangea/dnd';
import {
    getBlockRealId,
    moveBlocksToIndex,
    type InsertTarget,
} from '@/components/preview/block-selection/blockClipboard';
import { ReportData } from '@/types/report';
import { generateNewBlock } from '../utils/generateNewBlock';

export function useReportDragAndDrop(
    _formData: Partial<ReportData>,
    handleUpdate: (
        updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>),
    ) => void,
    onRequestChartCreation: () => void,
) {
    const handleDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;

        handleUpdate((prev) => {
            const currentTests = prev.tests || {
                section_title: '',
                blocks: [],
            };
            const blocks = Array.from(currentTests.blocks || []);

            // Helper to parse offset from droppableId "test_blocks_{offset}"
            const getOffset = (droppableId: string) => {
                const match = droppableId.match(/^test_blocks_(\d+)/);
                if (match?.[1]) return parseInt(match[1], 10) || 0;
                return 0;
            };

            // Dragging New Block from Toolbox
            if (
                source.droppableId === 'toolbox' &&
                destination.droppableId.startsWith('test_blocks_')
            ) {
                if (draggableId === 'tool_chart') {
                    onRequestChartCreation();
                    return {};
                }

                const offset = getOffset(destination.droppableId);
                const globalIndex = offset + destination.index;

                const newBlock = generateNewBlock(draggableId, globalIndex);

                // Ensure we don't insert out of bounds if possible,
                // though splice usually handles it by appending
                blocks.splice(globalIndex, 0, newBlock);

                const updatedBlocks = blocks.map((block, index) => ({
                    ...block,
                    order: index,
                }));

                return {
                    tests: {
                        ...currentTests,
                        blocks: updatedBlocks,
                    },
                };
            }

            // Reordering an existing block. dnd indices are chunk-local and lie
            // near split fragments, so they are only used to derive a real-id
            // anchor; the actual move is resolved by moveBlocksToIndex, which
            // carries a table's linked companions and preserves ids.
            if (
                source.droppableId.startsWith('test_blocks_') &&
                destination.droppableId.startsWith('test_blocks_')
            ) {
                const sameList = source.droppableId === destination.droppableId;
                if (sameList && destination.index === source.index) return {};

                const destOffset = getOffset(destination.droppableId);
                // Global index of the block the drop lands BEFORE. Within a
                // chunk, local index j maps to global offset + j (continuation
                // fragments point at their origin block). Same-list drops use
                // dnd's final-position semantics: moving down lands after the
                // element at destination.index.
                const movedDown = sameList && destination.index > source.index;
                const beforeGlobal = destOffset + destination.index + (movedDown ? 1 : 0);

                const target: InsertTarget =
                    beforeGlobal <= 0
                        ? 'start'
                        : beforeGlobal >= blocks.length
                          ? 'end'
                          : { afterRealId: getBlockRealId(blocks[beforeGlobal - 1]) };

                const next = moveBlocksToIndex(blocks, [draggableId], target);
                if (next === blocks) return {};

                return {
                    tests: {
                        ...currentTests,
                        blocks: next,
                    },
                };
            }

            return {};
        });
    };

    return { handleDragEnd };
}
